import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

export default function SettingsForm() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then(({ data }) => {
        setS({
          ...data,
          categories: {
            imovel: { ...data.categories.imovel, max_credit: data.categories.imovel.max_credit ?? "" },
            veiculo: { ...data.categories.veiculo, max_credit: data.categories.veiculo.max_credit ?? "" },
          },
        });
      })
      .catch((e) => toast.error(formatApiError(e, "Erro ao carregar configurações.")));
  }, []);

  if (!s) {
    return (
      <div className="flex justify-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const set = (path, value) => {
    setS((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const num = (v, fallback = 0) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? fallback : Number(v));

  async function save() {
    setSaving(true);
    try {
      const payload = {
        whatsapp_number: String(s.whatsapp_number || "").replace(/\D/g, ""),
        notification_email: s.notification_email,
        ga_id: s.ga_id || "",
        pixel_id: s.pixel_id || "",
        video_url: s.video_url || "",
        thresholds: { hot: num(s.thresholds.hot, 70), warm: num(s.thresholds.warm, 40) },
        weights: {
          parcela: num(s.weights.parcela, 30),
          prazo: num(s.weights.prazo, 25),
          lance: num(s.weights.lance, 20),
          carta: num(s.weights.carta, 15),
          engajamento: num(s.weights.engajamento, 10),
        },
        categories: {
          imovel: {
            parcela_ratio_ref: num(s.categories.imovel.parcela_ratio_ref, 0.006),
            min_credit: num(s.categories.imovel.min_credit, 200000),
            max_credit: s.categories.imovel.max_credit === "" ? null : num(s.categories.imovel.max_credit, null),
          },
          veiculo: {
            parcela_ratio_ref: num(s.categories.veiculo.parcela_ratio_ref, 0.013),
            min_credit: num(s.categories.veiculo.min_credit, 50000),
            max_credit: s.categories.veiculo.max_credit === "" ? null : num(s.categories.veiculo.max_credit, null),
          },
        },
      };
      await api.put("/admin/settings", payload);
      setS({ ...s, ...payload });
      toast.success("Configurações salvas! O site já usa os novos valores.");
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao salvar configurações."));
    } finally {
      setSaving(false);
    }
  }

  const catLabels = { imovel: "Imóvel", veiculo: "Veículo" };

  return (
    <div className="space-y-6" data-testid="admin-settings-view">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6">
          <h3 className="font-display text-lg font-bold text-slate-900">Contato e notificações</h3>
          <Field label="WhatsApp da Isabela" hint="Somente números, com 55 + DDD. Usado nos botões da página e nos links de lead.">
            <Input data-testid="settings-whatsapp-input" value={s.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="5571983670243" />
          </Field>
          <Field label="E-mail de notificação" hint="Recebe leads quentes e o resumo diário.">
            <Input data-testid="settings-email-input" type="email" value={s.notification_email} onChange={(e) => set("notification_email", e.target.value)} placeholder="isaguedes1319@gmail.com" />
          </Field>
          <Field label="Google Analytics ID" hint="Opcional. Ex.: G-XXXXXXX. Eventos de chat e lead por categoria.">
            <Input data-testid="settings-ga-input" value={s.ga_id} onChange={(e) => set("ga_id", e.target.value)} placeholder="G-..." />
          </Field>
          <Field label="Meta Pixel ID" hint="Opcional. Ex.: 1234567890. Evento Lead por categoria.">
            <Input data-testid="settings-pixel-input" value={s.pixel_id} onChange={(e) => set("pixel_id", e.target.value)} placeholder="1234567890" />
          </Field>
        </div>

        <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6">
          <h3 className="font-display text-lg font-bold text-slate-900">Classificação (score 0–100)</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Quente a partir de">
              <Input data-testid="settings-threshold-hot" type="number" min="0" max="100" value={s.thresholds.hot} onChange={(e) => set("thresholds.hot", e.target.value)} />
            </Field>
            <Field label="Morno a partir de">
              <Input data-testid="settings-threshold-warm" type="number" min="0" max="100" value={s.thresholds.warm} onChange={(e) => set("thresholds.warm", e.target.value)} />
            </Field>
          </div>
          <h4 className="pt-2 text-xs font-bold uppercase tracking-wide text-slate-400">Pesos por critério (máx. 100 no total)</h4>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {["parcela", "prazo", "lance", "carta", "engajamento"].map((w) => (
              <Field key={w} label={w}>
                <Input data-testid={`settings-weight-${w}`} type="number" min="0" max="100" value={s.weights[w]} onChange={(e) => set(`weights.${w}`, e.target.value)} />
              </Field>
            ))}
          </div>
        </div>
      </div>

      {["imovel", "veiculo"].map((cat) => (
        <div key={cat} className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={`settings-category-${cat}`}>
          <h3 className="font-display text-lg font-bold text-slate-900">Score de {catLabels[cat]}</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field
              label="Parcela mínima compatível (% da carta)"
              hint={cat === "imovel" ? "Referência: 0,6% — prazos longos." : "Referência: 1,3% — prazos mais curtos."}
            >
              <Input
                data-testid={`settings-ratio-${cat}`}
                type="number"
                step="0.01"
                min="0.01"
                max="20"
                value={Number(s.categories[cat].parcela_ratio_ref * 100).toFixed(2)}
                onChange={(e) => set(`categories.${cat}.parcela_ratio_ref`, Number(e.target.value) / 100)}
              />
            </Field>
            <Field label="Carta mínima atendida (R$)">
              <Input data-testid={`settings-min-credit-${cat}`} type="number" min="0" value={s.categories[cat].min_credit} onChange={(e) => set(`categories.${cat}.min_credit`, e.target.value)} />
            </Field>
            <Field label="Carta máxima atendida (R$)" hint="Deixe vazio para não limitar.">
              <Input data-testid={`settings-max-credit-${cat}`} type="number" min="0" value={s.categories[cat].max_credit} onChange={(e) => set(`categories.${cat}.max_credit`, e.target.value)} placeholder="Sem limite" />
            </Field>
          </div>
        </div>
      ))}

      <button
        data-testid="settings-save-button"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar configurações
      </button>
    </div>
  );
}
